import { useState, useMemo, useEffect } from "react";
import Modal from "../../../components/ui/modal";
import Dropdown from "../../../components/ui/dropdown";
import colors from "../../../utils/colors";
import Button from "../../../components/ui/button";
import { TPatientDetails } from "../../../types";
import useValidateStateObj from "../../../hooks/useValidateStateObj";
import { updateStateValue } from "../utils";
import { useUpdatePatient } from "../../../apis/patients";
import { useFormik } from "formik";
import * as yup from "yup";
import { Text } from "@chakra-ui/react";

interface IProps {
  isOpen: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  patient: TPatientDetails;
}

const UpdatePatientModal = ({ isOpen, onClose, title, patient }: IProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [updatePatientDetails, setUpdatePatientDetails] =
    useState<TPatientDetails>({} as TPatientDetails);
  useValidateStateObj<TPatientDetails>(updatePatientDetails);

  const clearInputs = () => {
    setUpdatePatientDetails(updateStateValue);
    setSubmitting(false);
  };
  const handleClose = () => {
    onClose(false);
    clearInputs();
  };

  const { mutate: updatePatient, isPending } = useUpdatePatient({
    handleSuccess: () => {
      handleClose();
    },
  });

  useEffect(() => {
    if (patient) {
      setUpdatePatientDetails({
        patientId: patient["Patient ID"],
        name: patient["Patient Name"],
        dateOfBirth: patient["Patient DOB"],
        phoneNumber: patient["Phone Number"],
        height: patient["Patient Height"],
        weight: patient["Patient Weight"],
        gender: patient["Patient Gender"],
        email: patient["Patient email"],
        address: patient["Patient Address"],
        bloodGenotype: patient["Patient Blood Genotype"],
        bloodGroup: patient["Patient Blood Group"],
        riskCategory: patient["Category"],
        patientNotes: patient["Patient Notes"],
      });
    }
  }, [patient]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: updatePatientDetails,
    validationSchema: yup.object({
      name: yup.string().required("Patient's name is required"),
      email: yup.string().required("Patient's email is required"),
      phoneNumber: yup.string().required("Patient's phone number is required"),
      height: yup.number().min(1, "Patient's height is required").required(),
      weight: yup.number().min(1, "Patient's weight is required").required(),
      bloodGroup: yup.string().required("Patient's blood group is required"),
      bloodGenotype: yup
        .string()
        .required("Patient's blood genotype is required"),
      gender: yup.string().required("Patient's gender is required"),
      address: yup.string().required("Patient's address is required"),
      dateOfBirth: yup.mixed().required("Patient's date of birth is required"),
      riskCategory: yup
        .string()
        .required("Patient's risk category is required"),
      patientNotes: yup.string().max(10000, "Notes too long").optional(), // ✅ NEW
    }),
    onSubmit: (values) => {
      delete values?.dateOfBirth;
      updatePatient(
        {
          ...values,
          height: Number(values?.height),
          weight: Number(values?.weight),
          patientNotes: String(values?.patientNotes),
        },
        {
          onSuccess: () => handleClose(),
        }
      );
    },
  });
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      className="w-[35%] min-w-[400px] max-w-[500px] h-[70%] max-h-[700px] p-[40px]"
    >
      <div className="flex flex-col gap-4 mt-6">
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's name</p>
          <input
            onChange={(e) => formik.setFieldValue("name", e.target.value)}
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's name`}
            value={formik?.values?.name}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.name && formik?.touched?.name && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.name}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's email</p>
          <input
            onChange={(e) => formik.setFieldValue("email", e.target.value)}
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's email`}
            value={formik?.values?.email}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.email && formik?.touched?.email && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.email}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's phone number</p>
          <input
            onChange={(e) =>
              formik.setFieldValue("phoneNumber", e.target.value)
            }
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's phone number`}
            value={formik?.values?.phoneNumber}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.phoneNumber && formik?.touched?.phoneNumber && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.phoneNumber}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's height</p>
          <input
            onChange={(e) =>
              formik.setFieldValue("height", Number(e.target.value))
            }
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's height (cm)`}
            value={formik?.values?.height}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.height && formik?.touched?.height && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.height}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's weight</p>
          <input
            onChange={(e) =>
              formik.setFieldValue("weight", Number(e.target.value))
            }
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's weight (Kg)`}
            value={formik?.values?.weight}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.weight && formik?.touched?.weight && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.weight}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's address</p>
          <input
            onChange={(e) => formik.setFieldValue("address", e.target.value)}
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's address`}
            value={formik?.values?.address}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.address && formik?.touched?.address && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.address}
            </Text>
          )}
        </div>

        <div className="w-full">
          <p className="text-sm mb-2">Blood group:</p>
          <Dropdown
            onSelect={(s) => formik.setFieldValue("bloodGroup", s)}
            options={["A+", "B+", "AB+", "O+", "A-", "B-", "AB-", "O-"]}
            className="!h-[40px] !w-full"
            value={formik?.values?.bloodGroup}
          />
          {formik?.errors?.bloodGroup && formik?.touched?.bloodGroup && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.bloodGroup}
            </Text>
          )}
        </div>
        <div className="w-full">
          <p className="text-sm mb-2">Blood genotype:</p>
          <Dropdown
            onSelect={(s) => formik.setFieldValue("bloodGenotype", s)}
            options={["AA", "AC", "AS", "SS", "CC", "SC"]}
            className="!h-[40px] !w-full"
            value={formik?.values?.bloodGenotype}
          />
          {formik?.errors?.bloodGenotype && formik?.touched?.bloodGenotype && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.bloodGenotype}
            </Text>
          )}
        </div>
        <div className="flex w-full">
          <div className="w-full">
            <p className="text-sm mb-2">Gender:</p>
            <Dropdown
              onSelect={(s) => formik.setFieldValue("gender", s)}
              options={["Male", "Female"]}
              className="!h-[40px] !w-full"
              value={formik?.values?.gender}
            />
            {formik?.errors?.gender && formik?.touched?.gender && (
              <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
                {formik?.errors?.gender}
              </Text>
            )}
          </div>
        </div>
        <div className="flex w-full">
          <div className="w-full">
            <p className="text-sm mb-2">Risk category:</p>
            <Dropdown
              onSelect={(s) => formik.setFieldValue("riskCategory", s)}
              options={["Low Risk", "Medium Risk", "High Risk"]}
              className="!h-[40px] !w-full"
              value={formik?.values?.riskCategory}
            />
            {formik?.errors?.riskCategory && formik?.touched?.riskCategory && (
              <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
                {formik?.errors?.riskCategory}
              </Text>
            )}
          </div>
        </div>

        {/* ✅ NEW: Patient Notes */}
        <div className="w-full">
          <p className="text-sm mb-2">Patient Notes (optional)</p>
          <textarea
            value={formik.values.patientNotes}
            onChange={(e) =>
              formik.setFieldValue("patientNotes", e.target.value)
            }
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder="Enter any notes for this patient"
            className="placeholder:text-[#606060] text-[14px] min-h-[100px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.patientNotes && formik?.touched?.patientNotes && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.patientNotes}
            </Text>
          )}
        </div>

        <Button
          text="Update patient information"
          theme="primary"
          size="md"
          className="rounded-md"
          loading={isPending}
          onClick={() => formik?.handleSubmit()}
          type="submit"
        />
      </div>
    </Modal>
  );
};

export default UpdatePatientModal;
